import { useEffect, useRef, useState } from "react";
import {
  ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL,
  ADVERSE_MEDIA_KEYWORD_LABELS,
  daysBackLabel,
  listCompanySearchConfigurationAudits,
  listSearchConfigurations,
  type SearchConfigurationAudit,
} from "@/src/services/search_configurations_service";

const FIELD_LABELS: Record<string, string> = {
  adverse_media_search: "Adverse media settings",
  pep_search: "PEP settings",
  sanctions_search: "Sanctions settings",
  description: "Description",
  api_version: "API version",
};
// identity fields of the config, not something the customer changed
const HIDDEN_FIELDS = new Set(["company", "user"]);

const ACTION_LABELS: Record<string, string> = {
  Create: "Created",
  Update: "Updated",
  Delete: "Deleted",
  "Soft delete": "Deleted",
};

const SUBFIELD_LABELS: Record<string, string> = {
  keywords: "Keywords",
  country: "Countries",
  language: "Languages",
  min_score: "Confidence score",
  time_horizon: "Time range",
  data_source: "Data sources",
  entity_type: "Entity type",
};

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function toList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string" && v) return v.split(",").map((x) => x.trim());
  return [];
}

function formatValue(key: string, v: unknown): string {
  if (v == null || v === "") return "none";
  if (key === "min_score" && typeof v === "number") return `${Math.round(v * 100)}%`;
  if (key === "time_horizon" && typeof v === "number") return daysBackLabel(v);
  if (key === "keywords")
    return toList(v)
      .map((k) => ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k)
      .join(", ");
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

/** One human line per sub-field that differs between `before` and `after`. */
function diffSection(before: unknown, after: unknown): string[] {
  const a = isObject(before) ? before : {};
  const b = isObject(after) ? after : {};
  const lines: string[] = [];
  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    const label = SUBFIELD_LABELS[key] ?? key;
    const oldV = a[key];
    const newV = b[key];
    if (JSON.stringify(oldV) === JSON.stringify(newV)) continue;
    if (key === "country" && (oldV === null || newV === null)) {
      const show = (v: unknown) =>
        v === null ? ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL : formatValue(key, v) || "worldwide";
      lines.push(`${label}: ${show(oldV)} → ${show(newV)}`);
    } else if (Array.isArray(oldV) || Array.isArray(newV) || key === "keywords") {
      const oldL = toList(oldV);
      const newL = toList(newV);
      const added = newL.filter((x) => !oldL.includes(x));
      const removed = oldL.filter((x) => !newL.includes(x));
      const parts = [
        added.length ? `added ${formatValue(key, added)}` : "",
        removed.length ? `removed ${formatValue(key, removed)}` : "",
      ].filter(Boolean);
      if (parts.length) lines.push(`${label}: ${parts.join("; ")}`);
    } else {
      lines.push(`${label}: ${formatValue(key, oldV)} → ${formatValue(key, newV)}`);
    }
  }
  return lines;
}

interface AuditEntry {
  title: string;
  details: string[];
}

function describe(audit: SearchConfigurationAudit): AuditEntry {
  const action = ACTION_LABELS[audit.action] ?? audit.action;
  const fields = audit.changedFields.filter((f) => !HIDDEN_FIELDS.has(f));
  const title =
    fields.length > 0 ? `${action}: ${fields.map((f) => FIELD_LABELS[f] ?? f).join(", ")}` : action;
  const details = fields.flatMap((f) => {
    const { old: oldV, new: newV } = audit.changes[f] ?? {};
    if (isObject(oldV) || isObject(newV)) {
      if (newV == null) return [`${FIELD_LABELS[f] ?? f} removed`];
      return diffSection(oldV, newV);
    }
    return [`${FIELD_LABELS[f] ?? f}: ${formatValue(f, oldV)} → ${formatValue(f, newV)}`];
  });
  return { title, details };
}

function formatTimestamp(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export function SearchSettingsChangelog({ reloadKey }: { reloadKey: number }) {
  const [audits, setAudits] = useState<SearchConfigurationAudit[] | null>(null);
  const [configId, setConfigId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  // bumped on every reload so a "load more" still in flight can't append to a fresh list
  const generation = useRef(0);

  useEffect(() => {
    let cancelled = false;
    generation.current += 1;
    (async () => {
      const list = await listSearchConfigurations();
      const id = list.ok ? (list.value.find((c) => c.type === "company")?.id ?? null) : null;
      // no company config yet (or it was deleted): there is no history to show
      const res = id ? await listCompanySearchConfigurationAudits(id) : null;
      if (cancelled) return;
      setConfigId(id);
      setPage(1);
      setMoreError(false);
      setLoadingMore(false);
      setAudits(res?.ok ? res.value.audits : null);
      setHasMore(res?.ok ? res.value.hasMore : false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function loadMore() {
    if (!configId || loadingMore) return;
    const gen = generation.current;
    setLoadingMore(true);
    setMoreError(false);
    const res = await listCompanySearchConfigurationAudits(configId, page + 1);
    if (gen !== generation.current) return;
    setLoadingMore(false);
    if (!res.ok) {
      setMoreError(true);
      return;
    }
    setAudits((prev) => [...(prev ?? []), ...res.value.audits]);
    setPage(page + 1);
    setHasMore(res.value.hasMore);
  }

  return (
    <p className="text-small text-grey-900">
      These settings define the defaults applied to your company&apos;s screenings, monitoring and
      API searches.
      {audits && (
        <>
          {" "}
          See the{" "}
          <span className="group relative inline-block">
            <button
              type="button"
              aria-controls="search-settings-changelog"
              className="rounded-sm bg-yellow-100 px-1 font-medium text-black underline decoration-dotted underline-offset-2"
            >
              changelog
            </button>
            <span
              id="search-settings-changelog"
              role="region"
              aria-label="Changelog"
              className="invisible absolute left-0 top-full z-20 mt-1 max-h-72 w-80 max-w-[80vw] overflow-y-auto rounded-md border border-grey-200 bg-white p-3 text-left opacity-0 shadow-lg transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
            >
              {audits.length === 0 ? (
                <span className="text-small text-grey-600">No changes recorded yet.</span>
              ) : (
                <ul className="flex flex-col gap-2">
                  {audits.map((a) => {
                    const { title, details } = describe(a);
                    return (
                      <li key={a.id} className="flex flex-col">
                        <span className="text-small text-black">{title}</span>
                        {details.map((d) => (
                          <span key={d} className="text-small text-grey-900">
                            {d}
                          </span>
                        ))}
                        <span className="text-small text-grey-600">
                          {formatTimestamp(a.timestamp)}
                          {a.userEmail ? ` · ${a.userEmail}` : ""}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {moreError && (
                <span className="mt-2 block text-small text-red-700" role="alert">
                  Couldn&apos;t load more changes.
                </span>
              )}
              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="mt-3 w-full rounded-md border border-grey-200 py-1 text-small text-black hover:bg-grey-50 disabled:opacity-60"
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </span>
          </span>
          .
        </>
      )}
    </p>
  );
}
